import { supabase } from '../../shared/lib/supabase'

const API_BASE = import.meta.env.VITE_API_BASE_URL as string

export interface RecordingUploadResult {
  id: string
  storagePath: string
  status: string
  createdAt: string
}

export interface Recording {
  id: string
  storagePath: string
  status: 'uploaded' | 'transcribing' | 'processing' | 'done' | 'failed'
  mimeType: string
  fileSize: number
  durationSeconds: number | null
  transcriptText: string | null
  transcriptLanguage: string | null
  notebookEntryId: string | null
  createdAt: string
}

/**
 * Upload one or more audio segments for a single recording.
 *
 * `segments` is an array because a recording may have been split across multiple
 * `MediaRecorder` lifecycles if the mic stream was interrupted mid-recording
 * (iOS backgrounding, incoming call, etc.). Segments from different MediaRecorder
 * instances cannot be naively concatenated client-side — each has its own container
 * init segment — so we upload them separately and let the server stitch them with
 * ffmpeg. A recording without interruptions is just a single-element array.
 */
export async function uploadRecording(
  segments: Blob[],
  durationSeconds: number,
  linkedEntryId?: string | null,
): Promise<RecordingUploadResult> {
  if (segments.length === 0) throw new Error('No audio segments to upload')

  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const formData = new FormData()
  // All segments in a recording share the same mimeType (same device, same session),
  // but compute the extension per-segment defensively.
  segments.forEach((segment, i) => {
    const ext = segment.type.startsWith('audio/mp4') ? 'm4a'
      : segment.type.startsWith('audio/ogg') ? 'ogg'
      : 'webm'
    formData.append('file', segment, `segment-${i}.${ext}`)
  })
  formData.append('duration', String(durationSeconds))
  if (linkedEntryId) formData.append('linkedEntryId', linkedEntryId)

  const res = await fetch(`${API_BASE}/recording/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      // Do NOT set Content-Type — browser sets it with multipart boundary
    },
    body: formData,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Upload failed: ${res.status}`)
  }

  return res.json()
}

export interface RecordingWithAudioUrl extends Recording {
  audioUrl: string
}

export async function getRecordingForEntry(entryId: string): Promise<RecordingWithAudioUrl> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/recording/by-entry/${entryId}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Failed to fetch recording: ${res.status}`)
  }

  return res.json()
}

export async function getRecording(id: string): Promise<Recording> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/recording/${id}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Failed to fetch recording: ${res.status}`)
  }

  return res.json()
}

export interface PendingRecording {
  id: string
  status: 'uploaded' | 'transcribing' | 'processing'
  durationSeconds: number | null
  createdAt: string
  linkedEntryId: string | null
}

export async function getPendingRecordings(): Promise<PendingRecording[]> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/recording/pending`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Failed to fetch pending recordings: ${res.status}`)
  }

  return res.json()
}

export async function regenerateRecordingSummary(recordingId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/recording/${recordingId}/regenerate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Failed to regenerate summary: ${res.status}`)
  }
}
